import { Carousel, Embla } from '@mantine/carousel';
import { Divider, Flex, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import Autoplay from 'embla-carousel-autoplay';
import { useEffect, useRef, useState } from 'react';
import './GetStarted.scss';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Elements';
import { ASSETS_BUCKET_URL } from '@/config';
import { ssoLogin } from '@/modules/auth';
import storage from '@/utils/storage';

export const GetStarted = () => {
  const config = storage.getItem('config');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isSmallDeviceMobile = useMediaQuery('(max-width: 350px)');
  const autoplay = useRef(Autoplay({ delay: 3000 }));
  const imageAutoplay = useRef(Autoplay({ delay: 3000 }));
  const [embla, setEmbla] = useState<Embla>();
  const [imageEmbla, setImageEmbla] = useState<Embla>();

  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  const stopAutoplay = () => {
    autoplay.current.stop();
    imageAutoplay.current.stop();
  };

  const startAutoplay = () => {
    autoplay.current.play();
    imageAutoplay.current.play();
  };

  useEffect(() => {
    if (embla && imageEmbla) {
      if (currentSlide === 0) {
        imageEmbla.scrollTo(currentIndex);
      }
      if (currentSlide === 1) {
        embla.scrollTo(currentIndex);
      }
    }
  }, [currentIndex, currentSlide, embla, imageEmbla]);

  return (
    <Flex
      justify="space-between"
      align="center"
      className="wrapper disable-scroll"
      // px={{ base: 'sm', sm: 'lg' }}
    >
      {!isMobile && (
        <div className="wrapper-img">
          <Carousel
            loop
            withControls={false}
            withIndicators={false}
            onMouseEnter={stopAutoplay}
            onMouseLeave={startAutoplay}
            plugins={[imageAutoplay.current]}
            align="start"
            getEmblaApi={setEmbla}
            onSlideChange={(index) => {
              setCurrentIndex(index);
              setCurrentSlide(0);
            }}
          >
            <Carousel.Slide>
              <img src={`${ASSETS_BUCKET_URL}/svg/Group1.svg`} alt="Group1" loading="lazy" />
            </Carousel.Slide>
            <Carousel.Slide>
              <img src={`${ASSETS_BUCKET_URL}/svg/Group2.svg`} alt="Group2" loading="lazy" />
            </Carousel.Slide>
            <Carousel.Slide>
              <img src={`${ASSETS_BUCKET_URL}/svg/Group3.svg`} alt="Group3" loading="lazy" />
            </Carousel.Slide>
            <Carousel.Slide>
              <img src={`${ASSETS_BUCKET_URL}/svg/Group4.svg`} alt="Group4" loading="lazy" />
            </Carousel.Slide>
          </Carousel>
        </div>
      )}
      <Flex direction="column" className="wrapper-content">
        <Carousel
          withIndicators
          withControls={false}
          loop
          align="start"
          breakpoints={[
            { maxWidth: 'md', slideSize: '100%' },
            { maxWidth: 'sm', slideSize: '100%', slideGap: 0 },
          ]}
          plugins={isMobile ? [autoplay.current] : []}
          getEmblaApi={setImageEmbla}
          onMouseEnter={stopAutoplay}
          onMouseLeave={startAutoplay}
          onBlur={startAutoplay}
          onSlideChange={(index) => {
            setCurrentIndex(index);
            setCurrentSlide(1);
          }}
          styles={{
            root: {
              height: !isMobile ? '250px' : '',
            },
            indicators: {
              justifyContent: 'flex-start',
              gap: isMobile ? '4px' : '10px',
              // eslint-disable-next-line no-nested-ternary
              bottom: isSmallDeviceMobile ? 'calc(-0.5rem)' : isMobile ? '0' : '1rem',
              padding: '0 1rem',
            },
            indicator: {
              width: isMobile ? 10 : 16,
              height: isMobile ? 6 : 12,
              transition: 'width 250ms ease',
              background: '#3a15931a',
              '&[data-active]': {
                width: isMobile ? 20 : 40,
                background: 'var(--mantine-color-primary-7)',
              },
            },
          }}
        >
          <Carousel.Slide>
            <Flex direction="column">
              {isMobile && (
                <Flex
                  align="center"
                  className="get-started-images-mobile-container"
                  justify="center"
                >
                  <img
                    src={`${ASSETS_BUCKET_URL}/svg/Group1.svg`}
                    alt="Group1"
                    loading="lazy"
                    className="get-started-images-mobile"
                  />
                </Flex>
              )}

              <div className="banner-text-content">
                <Title weight={600} order={1} lh={1.5}>
                  Order Right <br />
                  From Your
                  <Text span c="primary" weight="bold" ml="sm" lh={1.5}>
                    Seat
                  </Text>
                </Title>
                <Text c="gray" component="p" fz="xs" mt="xs" mb="lg">
                  Register yourself and open up to a hassle free ordering process.
                </Text>
              </div>
            </Flex>
          </Carousel.Slide>
          <Carousel.Slide>
            <Flex direction="column">
              {isMobile && (
                <Flex
                  align="center"
                  className="get-started-images-mobile-container"
                  justify="center"
                >
                  <img
                    src={`${ASSETS_BUCKET_URL}/svg/Group2.svg`}
                    alt="Group2"
                    loading="lazy"
                    className="get-started-images-mobile"
                  />
                </Flex>
              )}
              <div className="banner-text-content">
                <Title weight={600} order={1} lh={1.5}>
                  Select From
                  <br />
                  <Text span c="primary" weight={700} mr="sm" lh={1.5}>
                    Curated
                  </Text>
                  Vendors
                </Title>
                <Text c="gray" component="p" mt="xs" mb="lg">
                  We handpick the best vendors so you enjoy healthy and hygenic food.
                </Text>
              </div>
            </Flex>
          </Carousel.Slide>
          <Carousel.Slide>
            <Flex direction="column">
              {isMobile && (
                <Flex
                  align="center"
                  className="get-started-images-mobile-container"
                  justify="center"
                >
                  <img
                    src={`${ASSETS_BUCKET_URL}/svg/Group3.svg`}
                    alt="Group3"
                    loading="lazy"
                    className="get-started-images-mobile"
                  />
                </Flex>
              )}
              <div className="banner-text-content">
                <Title weight={600} order={1} lh={1.5}>
                  Pay Using
                  <br />
                  Digital
                  <Text span c="primary" weight={700} ml="sm" lh={1.5}>
                    Wallets
                  </Text>
                </Title>
                <Text c="gray" component="p" mt="xs" mb="lg">
                  All your favourite payment methods in one place. Order and pay with just a click.
                </Text>
              </div>
            </Flex>
          </Carousel.Slide>
          <Carousel.Slide>
            <Flex direction="column">
              {isMobile && (
                <Flex
                  align="center"
                  className="get-started-images-mobile-container"
                  justify="center"
                >
                  <img
                    src={`${ASSETS_BUCKET_URL}/svg/Group4.svg`}
                    alt="Group4"
                    loading="lazy"
                    className="get-started-images-mobile"
                  />
                </Flex>
              )}
              <div className="banner-text-content">
                <Title weight={600} order={1} lh={1.5}>
                  <Text span c="primary" weight={700} mr="sm" lh={1.5}>
                    Scan QR
                  </Text>
                  & Enjoy
                  <br />
                  Delicious Food
                </Title>
                <Text c="gray" component="p" mt="xs" mb="lg">
                  Collect your delicious food by a unique QR code authentication process.
                </Text>
              </div>
            </Flex>
          </Carousel.Slide>
        </Carousel>
        {config && (
          <Flex
            direction="column"
            className="banner-text-content"
            mb="lg"
            mt={isSmallDeviceMobile ? 'lg' : ''}
          >
            <Flex
              mb={{
                base: 'sm',
                sm: 'md',
              }}
              columnGap={{
                base: 'sm',
                sm: 'md',
              }}
              className="get-started-buttons"
            >
              {config?.login_auth?.signup_enabled && (
                <Button
                  fullWidth
                  onClick={() => {
                    navigate('/auth/select-cafeteria');
                  }}
                >
                  {config?.is_sso_login ? 'Sign Up' : 'New User? Sign Up!'}
                </Button>
              )}

              {config?.is_sso_login && (
                <Button
                  fullWidth
                  onClick={() => {
                    ssoLogin();
                  }}
                >
                  {config?.sso_login_text ? config?.sso_login_text : 'SSO Login'}
                </Button>
              )}
            </Flex>

            <Flex
              columnGap={{
                base: 'sm',
                sm: 'md',
              }}
              className="get-started-buttons"
            >
              {config?.otp_login_allowed && !config?.is_sso_login_only && (
                <Button
                  variant="light"
                  fullWidth
                  onClick={() => {
                    navigate('/auth/login-with-otp');
                  }}
                >
                  Login with OTP
                </Button>
              )}
              {!config?.is_sso_login_only && (
                <Button
                  variant="light"
                  fullWidth
                  onClick={() => {
                    navigate('/auth/login');
                  }}
                >
                  Login
                </Button>
              )}
            </Flex>

            <Flex justify="center" align="center" w="100%" gap="md" mt="lg">
              {config?.terms_of_use && (
                <a href={config?.terms_of_use} className="no-underlined">
                  <Text c="primary" className="pointer" span td="none">
                    Terms of Use
                  </Text>
                </a>
              )}

              <Divider orientation="vertical" color="primary" size="xs" />

              {config?.privacy_policy && (
                <a href={config?.privacy_policy} className="no-underlined">
                  <Text c="primary" className="pointer" span td="none">
                    Privacy Policy
                  </Text>
                </a>
              )}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
